import { type FC, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useQuery } from "@apollo/client";

import { PageContentSpinner } from "../../layout/components/PageContentSpinner";
import { UserInfoContext } from "../../shared/api/contexts";
import { type MyLensesOrganizationsQuery, myLensesOrganizations } from "../../shared/api/queries";
import { EmbedGenericErrorMessage, EmbedGenericErrorTitle } from "../messages.generated";

import { EmbedErrorPanel } from "./EmbedErrorPanel";

/**
 * Redirects `/embed/remote-assets` to `/embed/:defaultOrgId/remote-assets`.
 * Used when the embedding host (e.g. Lens Studio) doesn't know the org ID upfront.
 */
export const EmbedDefaultOrgRedirect: FC = () => {
    const userInfo = useContext(UserInfoContext);
    const location = useLocation();
    const {
        data: orgsData,
        loading: orgsLoading,
        error: orgsError,
    } = useQuery<MyLensesOrganizationsQuery>(myLensesOrganizations, {
        variables: { limit: 0, cursor: "" },
    });

    const profileDefaultOrgId = userInfo?.data?.getUserProfile.defaultOrganizationId;
    const orgs = orgsData?.myLensesOrganizations.organizationList;

    // Stale-default guard: if the profile's default org is no longer in the user's
    // accessible list, fall back to the first accessible org instead of redirecting
    // to an orgId that will 403 every remote-assets query.
    const targetOrgId = orgs?.some((o) => o.id === profileDefaultOrgId) ? profileDefaultOrgId : orgs?.[0]?.id;

    if (orgsLoading) {
        return <PageContentSpinner />;
    }

    if (orgsError !== undefined || !targetOrgId) {
        return <EmbedErrorPanel title={<EmbedGenericErrorTitle />} message={<EmbedGenericErrorMessage />} />;
    }

    // Replace /embed/remote-assets[/...] with /embed/:orgId/remote-assets[/...]
    const restOfPath = location.pathname.replace(/^\/embed\/remote-assets/, "");
    const targetPath = `/embed/${targetOrgId}/remote-assets${restOfPath}`;

    return <Navigate to={targetPath + location.search + location.hash} replace />;
};
